import { Roles } from "constants/index";
import { normalizeCellString } from "./index";
import {
  AssociatedClient,
  AssociatedClientCreatePayload,
  AssociatedClientMatchItem,
  AssociatedClientMatchSource,
  User,
} from "types";

const ASSOCIATED_CLIENT_CREATOR_ROLES = new Set<string>([
  Roles.SUPERADMIN,
  Roles.ADMIN,
  Roles.SUPERVISOR,
]);

export type AssociatedClientLookupMap = Map<string, AssociatedClient>;

export const normalizeAssociatedClientName = (value: unknown) => {
  const normalized = normalizeCellString(value);
  return normalized ? normalized.trim() : null;
};

export const normalizeAssociatedClientForMatch = (value: unknown) =>
  String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, "")
    .replace(/\s+/g, " ")
    .trim();

const calculateLevenshteinDistance = (left: string, right: string) => {
  const matrix = Array.from({ length: right.length + 1 }, () =>
    Array(left.length + 1).fill(0),
  );

  for (let row = 0; row <= right.length; row += 1) {
    matrix[row][0] = row;
  }

  for (let column = 0; column <= left.length; column += 1) {
    matrix[0][column] = column;
  }

  for (let row = 1; row <= right.length; row += 1) {
    for (let column = 1; column <= left.length; column += 1) {
      const cost = right[row - 1] === left[column - 1] ? 0 : 1;

      matrix[row][column] = Math.min(
        matrix[row - 1][column] + 1,
        matrix[row][column - 1] + 1,
        matrix[row - 1][column - 1] + cost,
      );
    }
  }

  return matrix[right.length][left.length];
};

export const calculateAssociatedClientSimilarity = (
  leftValue: unknown,
  rightValue: unknown,
) => {
  const left = normalizeAssociatedClientForMatch(leftValue);
  const right = normalizeAssociatedClientForMatch(rightValue);

  if (!left || !right) return 0;
  if (left === right) return 1;

  const maxLength = Math.max(left.length, right.length);
  const editSimilarity =
    1 - calculateLevenshteinDistance(left, right) / maxLength;

  const leftTokens = new Set(left.split(" "));
  const rightTokens = new Set(right.split(" "));
  const intersection = Array.from(leftTokens).filter((token) =>
    rightTokens.has(token),
  ).length;
  const union = new Set([...Array.from(leftTokens), ...Array.from(rightTokens)])
    .size;

  const tokenSimilarity = union > 0 ? intersection / union : 0;
  const shortestLength = Math.min(left.length, right.length);

  const containmentSimilarity =
    shortestLength >= 3 && (left.includes(right) || right.includes(left))
      ? 0.75
      : 0;

  return Math.max(editSimilarity, tokenSimilarity, containmentSimilarity);
};

const ASSOCIATED_CLIENT_MATCH_THRESHOLD = 0.7;

export const buildAssociatedClientMatchItems = (
  sources: AssociatedClientMatchSource[],
  associatedClients: AssociatedClient[],
): AssociatedClientMatchItem[] => {
  const groupedSources = new Map<
    string,
    AssociatedClientMatchSource & {
      importedName: string;
      affectedRecords: number;
    }
  >();

  sources.forEach((source) => {
    const importedName = normalizeAssociatedClientName(
      source.associatedClientName,
    );

    if (!importedName) return;

    const normalizedName = normalizeAssociatedClientForMatch(importedName);

    const key = `${source.clientId}:${normalizedName}`;
    const previous = groupedSources.get(key);

    if (previous) {
      previous.affectedRecords += 1;
      return;
    }

    groupedSources.set(key, {
      ...source,
      importedName,
      affectedRecords: 1,
    });
  });

  return Array.from(groupedSources.entries()).reduce<
    AssociatedClientMatchItem[]
  >((items, [key, source]) => {
    const clientsFromSameOwner = associatedClients.filter(
      (associatedClient) =>
        associatedClient.client_id === source.clientId &&
        associatedClient.is_active,
    );

    const exactMatch = clientsFromSameOwner.some(
      (associatedClient) =>
        normalizeAssociatedClientForMatch(associatedClient.razon_social) ===
        normalizeAssociatedClientForMatch(source.importedName),
    );

    if (exactMatch) return items;

    const candidates = clientsFromSameOwner
      .map((associatedClient) => ({
        associatedClient,
        score: calculateAssociatedClientSimilarity(
          source.importedName,
          associatedClient.razon_social,
        ),
      }))
      .filter(
        (candidate) => candidate.score >= ASSOCIATED_CLIENT_MATCH_THRESHOLD,
      )
      .sort((left, right) => right.score - left.score);

    items.push({
      key,
      clientId: source.clientId,
      clientCode: source.clientCode,
      importedName: source.importedName,
      affectedRecords: source.affectedRecords,
      candidates,
    });

    return items;
  }, []);
};

export const canCreateAssociatedClients = (role?: User["role"] | null) =>
  !!role && ASSOCIATED_CLIENT_CREATOR_ROLES.has(role);

export const getAssociatedClientLookupKey = (
  clientId: number,
  value: unknown,
) => {
  const normalizedName = normalizeAssociatedClientName(value);

  if (!clientId || !normalizedName) {
    return null;
  }

  return `${clientId}:${normalizeAssociatedClientForMatch(normalizedName)}`;
};

export const buildAssociatedClientLookupMap = (
  associatedClients: AssociatedClient[],
) => {
  const lookupMap: AssociatedClientLookupMap = new Map();

  associatedClients.forEach((associatedClient) => {
    const lookupKey = getAssociatedClientLookupKey(
      associatedClient.client_id,
      associatedClient.razon_social,
    );

    if (!associatedClient.is_active || !lookupKey) {
      return;
    }

    lookupMap.set(lookupKey, associatedClient);
  });

  return lookupMap;
};

export const findAssociatedClientInLookupMap = (
  lookupMap: AssociatedClientLookupMap,
  clientId: number,
  value: unknown,
) => {
  const lookupKey = getAssociatedClientLookupKey(clientId, value);

  if (!lookupKey) {
    return null;
  }

  return lookupMap.get(lookupKey) ?? null;
};

export const buildAssociatedClientCreatePayload = (
  clientId: number,
  razonSocial: string,
): AssociatedClientCreatePayload => ({
  client_id: clientId,
  razon_social: razonSocial,
  address: "",
  cuit: "",
  contact: "",
  observations: "",
  is_active: true,
});
